import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { CartProvider } from './contexts/CartContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import PharmacySetupPage from './pages/PharmacySetupPage';
import Dashboard from './pages/Dashboard';
import PharmacyDashboard from './pages/pharmacy/PharmacyDashboard';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import RiderDashboard from './pages/rider/RiderDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
// import ProductsPage from './pages/customer/ProductsPage'; // Commented out - customers should chat with pharmacies instead
import CartPage from './pages/customer/CartPage';
import OrdersPage from './pages/customer/OrdersPage';
import EmergencyPage from './pages/EmergencyPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1E5BD7', // Logo blue
      light: '#64B5F6',
      dark: '#1548A6',
    },
    secondary: {
      main: '#FF6F00', // Orange for delivery
      light: '#FF9800',
      dark: '#E65100',
    },
    background: {
      default: '#F5F5F5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SocketProvider>
            <CartProvider>
              <ErrorBoundary>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/emergency" element={<EmergencyPage />} />
                <Route path="/payment/callback" element={<PaymentCallbackPage />} />

                {/* Pharmacy Setup Route */}
                <Route path="/pharmacy-setup" element={
                  <ProtectedRoute allowedUserTypes={['pharmacy']}>
                    <PharmacySetupPage />
                  </ProtectedRoute>
                } />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                {/* Pharmacy Routes */}
                <Route path="/pharmacy/*" element={
                  <ProtectedRoute allowedUserTypes={['pharmacy']}>
                    <PharmacyDashboard />
                  </ProtectedRoute>
                } />

                {/* Customer Routes */}
                <Route path="/customer/*" element={
                  <ProtectedRoute allowedUserTypes={['customer']}>
                    <CustomerDashboard />
                  </ProtectedRoute>
                } />
                {/* Products route redirected to customer dashboard - customers should chat with pharmacies instead */}
                <Route path="/products" element={
                  <ProtectedRoute allowedUserTypes={['customer']}>
                    <Navigate to="/customer" replace />
                  </ProtectedRoute>
                } />
                <Route path="/cart" element={
                  <ProtectedRoute allowedUserTypes={['customer']}>
                    <CartPage />
                  </ProtectedRoute>
                } />
                <Route path="/orders" element={
                  <ProtectedRoute allowedUserTypes={['customer']}>
                    <OrdersPage />
                  </ProtectedRoute>
                } />

                {/* Rider Routes */}
                <Route path="/rider/*" element={
                  <ProtectedRoute allowedUserTypes={['rider']}>
                    <RiderDashboard />
                  </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/admin" element={
                  <ProtectedRoute allowedUserTypes={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Router>
              </ErrorBoundary>
            </CartProvider>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;